/**
 * Whether the last thing the person did was point or type.
 *
 * `:focus-visible` is supposed to answer this, and for focus the browser moves itself it does.
 * It cannot answer it for focus moved by *script*: Chromium decides such a focus is "visible"
 * from the state of whatever was focused before, so a ring appears on a control nobody
 * navigated to with a keyboard. That is the whole of the header bug - see the note on
 * `DropdownMenuContent`, which is the only place this is read.
 *
 * Listeners are passive, capture-phase and registered once per module, so they see the gesture
 * before any handler can stop it and cost nothing per event.
 */
type Modality = 'pointer' | 'keyboard';

// Pointer until proven otherwise: a page nobody has touched yet must not start out drawing
// keyboard rings.
let modality: Modality = 'pointer';

if (typeof window !== 'undefined') {
	window.addEventListener('pointerdown', () => (modality = 'pointer'), { capture: true, passive: true });
	window.addEventListener(
		'keydown',
		(event) => {
			// A modifier on its own is a chord in progress - Cmd+R is not somebody navigating
			// by keyboard, and treating it as such would light a ring on the next close.
			if (event.ctrlKey || event.metaKey || event.altKey) return;
			modality = 'keyboard';
		},
		{ capture: true, passive: true },
	);
}

export const lastInputModality = (): Modality => modality;
